import React, { useCallback, useState } from 'react';
import type { FC, KeyboardEventHandler, ReactNode } from 'react';
import { Spinner, useBreakpointValue, useDisclosure } from '@chakra-ui/react';
import { useForm } from 'react-hook-form';
import { FormattedMessage, useIntl } from 'react-intl';
import {
    type CouponItemWithRecaptcha,
    useAddCoupon,
} from '../../../../bff-api/baskets/useAddCoupon';
import { useDeleteCoupon } from '../../../../bff-api/baskets/useDeleteCoupon';
import { getCouponPromotionAmount } from '../../../../bff-api/utils/basket-utils';
import { ConditionalRender } from '../../../../components/conditionally-render/ConditionalRender';
import { ContentAsset } from '../../../../components/content-asset';
import { Field } from '../../../../components/field';
import { usePromoCodeFields } from '../../../../components/forms/usePromoCodeFields';
import { GetIcon } from '../../../../components/icons';
import { useCart } from '../../../../hooks/useCart';
import {
    RecaptchaWorkflowState,
    useRecaptchaWorkflow,
} from '../../../../recaptcha/hooks/useRecaptchaWorkflow';
import { RecaptchaAction, RecaptchaFeature } from '../../../../recaptcha/recaptcha';
import { Button, Modal, Text, Tooltip, TooltipPlacement } from '../../../../ui-components';
import type { CouponItem } from '../../../../bff-api/customers/model';

export const CartPromoCode: FC = () => {
    const intl = useIntl();
    const { basket, currency } = useCart();
    const form = useForm<CouponItem>({ shouldFocusError: false });
    const fields = usePromoCodeFields({ form });
    const addCoupon = useAddCoupon();
    const deleteCoupon = useDeleteCoupon();
    const { isOpen, onOpen, onClose } = useDisclosure();
    const promotionAmount = getCouponPromotionAmount(basket);
    const [errorType, setErrorType] = useState<string>('');

    const {
        getRecaptchaPayload,
        handleApiError,
        handleApiSuccess,
        V2Widget,
        errorMessage: recaptchaError,
        state,
    } = useRecaptchaWorkflow({
        feature: RecaptchaFeature.PROMO_CODE,
        action: RecaptchaAction.PROMO_CODE_CART,
        autoSubmitOnV2: true,
        onV2Complete: async () => {
            await onApply();
        },
    });

    const [coupon] = basket?.couponItems ?? [];

    const clearErrors = (): void => {
        setErrorType('');
        form.clearErrors();
    };

    const onApply = form.handleSubmit(async (): Promise<void> => {
        const newCoupon = form.getValues();

        if (coupon) {
            setErrorType('multiple-coupons');
            form.setError('code', { type: 'validate' });
            return;
        }

        if (!newCoupon?.code) {
            setErrorType('invalid');
            form.setError('code', { type: 'validate' });
            return;
        }

        try {
            const recaptcha = await getRecaptchaPayload();

            const couponWithRecaptcha: CouponItemWithRecaptcha = {
                ...newCoupon,
                recaptcha,
            };

            await addCoupon.mutateAsync(couponWithRecaptcha);

            handleApiSuccess();
            form.reset({ code: '' });
            setErrorType('');
        } catch (err: unknown) {
            const wasHandled = handleApiError(err);

            if (!wasHandled) {
                setErrorType('invalid');
                form.setError('code', { type: 'validate' });
            }
        }
    });

    const Link = useCallback(
        (chunks: ReactNode[]) => (
            <Button
                id="promo-code-all-products-excluded-details"
                variant="text-button-no-hover"
                color="slt-red.1000"
                textSize="small"
                onClick={onOpen}
            >
                {chunks}
            </Button>
        ),
        [onOpen],
    );

    const onDelete = async ({ couponItemId }: CouponItem): Promise<void> => {
        await deleteCoupon.mutateAsync(couponItemId!);
        clearErrors();
    };

    const handleOnEnterPromo: KeyboardEventHandler<HTMLInputElement> = useCallback(
        async (e) => {
            if (e.key === 'Enter') {
                await onApply(e);
            }
        },
        [onApply],
    );

    const onBlur = (): void => {
        if (!form.getValues('code')) {
            clearErrors();
        }
    };

    const showAmountQualifiedIssueMessage =
        !!basket?.c_hasAmountQualifiedIssueInPromo && !basket.c_isAllProductsExcludedFromPromo;
    const showItemNotPromoEligibleMessage = basket?.c_isAllProductsExcludedFromPromo;

    const shouldShowPromoMessageError =
        showAmountQualifiedIssueMessage || showItemNotPromoEligibleMessage;

    return (
        <>
            <div className="grid grid-cols-[1fr_min-content] gap-x-4">
                <div className="col-span-2">
                    <Text id="promo-code-title" style="bold">
                        <FormattedMessage
                            id="cart_order_summary.promo_code"
                            defaultMessage="Promo code"
                        />
                    </Text>
                </div>
                <div className="mt-4">
                    <Field
                        {...{
                            ...fields?.code,
                            rules: {},
                        }}
                        variant="new-style"
                        inputProps={{
                            background: 'white',
                            onKeyDown: handleOnEnterPromo,
                            onBlur,
                        }}
                    />
                </div>
                <div className="mt-4">
                    <Button
                        id="promo-code-apply-button"
                        variant="secondary"
                        isLoading={form.formState.isSubmitting}
                        isDisabled={state === RecaptchaWorkflowState.V2_REQUIRED}
                        onClick={onApply}
                    >
                        <FormattedMessage id="cart_order_summary.apply" defaultMessage="Apply" />
                    </Button>
                </div>
                <ConditionalRender visibility={!!errorType}>
                    <div className="col-span-2 mt-1 flex items-center justify-start">
                        {errors[errorType] ?? null}
                    </div>
                </ConditionalRender>
                <ConditionalRender visibility={!!recaptchaError}>
                    <div className="col-span-2 mt-1 flex items-center justify-start">
                        <Text color="slt-red-error">{recaptchaError}</Text>
                    </div>
                </ConditionalRender>
                <ConditionalRender visibility={state === RecaptchaWorkflowState.V2_REQUIRED}>
                    <div className="col-span-2 mt-4">{V2Widget}</div>
                </ConditionalRender>
                <ConditionalRender visibility={!!coupon}>
                    <div className="mt-4">
                        <Text id={`promo-code-coupon-code-${coupon?.couponItemId}`}>
                            {coupon?.code}
                        </Text>
                    </div>
                    <div className="mt-4 justify-self-end">
                        <Button
                            id={`promo-code-coupon-remove-${coupon?.couponItemId}`}
                            variant="text-button-no-hover"
                            onClick={() => onDelete(coupon)}
                            isDisabled={deleteCoupon.isLoading}
                        >
                            <FormattedMessage
                                id="cart.item.secondary_buttons.remove"
                                defaultMessage="Remove"
                            />
                        </Button>
                        {deleteCoupon.isLoading ? (
                            <Spinner
                                marginLeft={1}
                                data-testid="cart-item-remove-button-loading"
                                size="xs"
                                color="slt-pepper.1000"
                            />
                        ) : null}
                    </div>
                    <div className="col-span-2 mt-1">
                        <Text
                            id={`promo-code-coupon-promotion-${coupon?.couponItemId}`}
                            style="bold"
                        >
                            {coupon?.c_promotionName}
                        </Text>
                    </div>
                    <div className="col-span-2 mt-1">
                        {coupon && !coupon.valid ? (
                            <Text id="promo-code-got-invalid" color="slt-red-error" size="small">
                                <FormattedMessage
                                    id="order_summary.promo_code.gotInvalid"
                                    defaultMessage="This promo code is invalid, please remove it."
                                />
                            </Text>
                        ) : null}
                        {showAmountQualifiedIssueMessage ? (
                            <Text
                                id="promo-code-amount-qualified-issue"
                                color="slt-red-error"
                                size="small"
                            >
                                <FormattedMessage
                                    id="cart_order_summary.promo_code.amount_qualified_issue"
                                    defaultMessage="Eligible product subtotal must exceed {promotionAmount}."
                                    values={{
                                        promotionAmount: intl.formatNumber(promotionAmount ?? 0, {
                                            style: 'currency',
                                            currency,
                                            minimumFractionDigits: 0,
                                            maximumFractionDigits: 0,
                                        }),
                                    }}
                                />
                            </Text>
                        ) : null}

                        {shouldShowPromoMessageError ? (
                            <Text
                                id="promo-code-all-products-excluded"
                                color="slt-red-error"
                                size="small"
                            >
                                <FormattedMessage
                                    id="cart_order_summary.promo_code.all_excluded"
                                    defaultMessage="Item(s) in your bag are not eligible for this promotion. <link>Details</link>"
                                    values={{
                                        link: Link,
                                    }}
                                />
                            </Text>
                        ) : null}
                    </div>
                </ConditionalRender>
            </div>
            {shouldShowPromoMessageError && basket?.c_promotionID ? (
                <Modal id="promo-code-details-modal" isOpen={isOpen} onClose={onClose}>
                    <ContentAsset contentAssetId={basket.c_promotionID} />
                </Modal>
            ) : null}
        </>
    );
};

const InvalidCouponMessage: FC = () => {
    const tooltipProps = useBreakpointValue({
        base: {
            placement: TooltipPlacement.topLeft,
        },
        sm: {
            placement: TooltipPlacement.top,
        },
        md: {
            placement: TooltipPlacement.topLeft,
        },
        'lg-sm': {
            placement: TooltipPlacement.top,
        },
    });
    return (
        <>
            <Text color="slt-red-error">
                <FormattedMessage
                    id="promo-code.invalid"
                    defaultMessage="The code you entered is not valid."
                />
            </Text>
            <Tooltip
                {...tooltipProps}
                label={
                    <Text size="small">
                        <FormattedMessage
                            id="promo-code.invalid.tooltip"
                            defaultMessage="Please verify your input and try again. It may contain an error, have already been redeemed, or is expired."
                        />
                    </Text>
                }
            >
                <div className="flex" data-testid="promo-code-invalid-tooltip">
                    <GetIcon name="info" size="md" />
                </div>
            </Tooltip>
        </>
    );
};

const errors: Record<string, ReactNode> = {
    invalid: <InvalidCouponMessage />,
    'multiple-coupons': (
        <Text color="slt-red-error">
            <FormattedMessage
                id="promo-code.multiple-coupons"
                defaultMessage="Only one promo can be applied at a time. Please remove promo to apply another."
            />
        </Text>
    ),
};
